import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReconnectingWebSocket from "reconnecting-websocket";
import { Connection } from "sharedb/lib/client";
import { Socket } from "sharedb/lib/sharedb";

import { type PubsubVirtualClassStateMessageFromClientType } from "../shared/types/av-state";
import { WebsocketStatusType } from "../shared/types/pubsub";
import "../shared/worksheets/ot-types/ot-patch";
import { patchShareDBConnectionForPubsub } from "../shared/worksheets/sharedb-patch";
import { type ConnectionState, setupConnectionListeners } from "../sharedb/ShareDBConnection";
import { setupSocketListeners } from "../utils/pubsub";

import { PingComponent } from "./PingComponent";
import { PubsubStatsCollectorProvider } from "./PubsubStatsCollectorProvider";

import { t } from "i18n/i18n";
import { useAppDispatch, useAppSelector } from "store/index";
import { clearPubsubAPICache } from "store/pubsub-slice";
import { selectCurrentCourseId, selectCurrentUserId, selectUserJWT } from "store/selectors";
import { logInfoWithDebugFlag } from "utils/logger";
import { useViewLevel } from "utils/urls";

// This mainly disables ShareDB throwing warnings when it receives pubsub messages that it
// doesn't know how to handle.
patchShareDBConnectionForPubsub();

// The minimum delay should be longer than the longest time we expect congested
// traffic to delay a message. Otherwise, ShareDB will think the connection is
// lost and try to reconnect, which can cause a loop of reconnecting and disconnecting.
const MIN_RECONNECTION_DELAY_MS = 3000;
// The maximum number of times we will try to reconnect before giving up, currently a total
// of MIN_RECONNECTION_DELAY_MS * MAX_RETRY_TIMES = 5 minutes.
const MAX_RETRY_TIMES = 100;

// eslint-disable-next-line react-refresh/only-export-components
export const globalShareDBState: { connection: Connection | null } = {
  connection: null,
};

// TODO: This should be unified with ConnectionStatusIndicator states
// eslint-disable-next-line react-refresh/only-export-components
export const CONNECTION_STATUS_MESSAGE: Record<WebsocketStatusType, string> = {
  connecting: t("websocket.connecting"),
  connected: t("websocket.connected"),
  disconnected: t("websocket.disconnected"),
  closed: t("websocket.closed"),
  stopped: t("websocket.stopped"),
  401: t("websocket.401"),
};
export interface ShareDBContextType {
  sharedbConnection: Connection | null;
  websocketStatus: WebsocketStatusType;
  websocketStatusReason: string;
  websocketStatusCourseId: string;
  retryConnection: () => void;
  sendWebsocketMessage: (data: PubsubVirtualClassStateMessageFromClientType) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const WebsocketContext = React.createContext<ShareDBContextType>({
  sharedbConnection: null,
  websocketStatus: "connecting",
  websocketStatusReason: "",
  websocketStatusCourseId: "",
  retryConnection: () => {},
  sendWebsocketMessage: () => {},
});

export const WebsocketProvider = ({ children }: { children: React.ReactNode }) => {
  const courseId = useAppSelector(selectCurrentCourseId);
  const jwt = useAppSelector(selectUserJWT);
  const userId = useAppSelector(selectCurrentUserId);
  const isHomeView = useViewLevel().home;

  const [sharedbConnection, setSharedbConnection] = useState<Connection | null>(null);
  const [websocketErrorCode, setWebsocketErrorCode] = useState<"401" | null>(null);
  const [sharedbStatus, setSharedbStatus] = useState<ConnectionState>("connecting");
  const [websocketStatusReason, setWebsocketStatusReason] = useState<string>("");
  const [websocketStatusCourseId, setWebsocketStatusCourseId] = useState<string>(courseId || "");
  const [webSocket, setWebSocket] = useState<ReconnectingWebSocket | null>(null);
  const [connectionKey, setConnectionKey] = useState(0);
  const dispatch = useAppDispatch();

  const retryConnection = useCallback(() => {
    setConnectionKey((key) => key + 1);
  }, []);

  const sendWebsocketMessage = useCallback(
    (message: PubsubVirtualClassStateMessageFromClientType) => {
      if (!webSocket || webSocket.readyState !== WebSocket.OPEN) {
        logInfoWithDebugFlag(
          "debug_virtual_class",
          "Websocket is not open, cannot send pubsub message",
          message,
        );

        return;
      }

      logInfoWithDebugFlag("debug_virtual_class", "Sending pubsub message via websocket", message);
      webSocket.send(JSON.stringify(message));
    },
    [webSocket],
  );

  // websocketStatus is computed from error code and sharedb status
  const websocketStatus: WebsocketStatusType = websocketErrorCode || sharedbStatus;

  const contextValue = useMemo(
    () => ({
      sharedbConnection,
      websocketStatus,
      websocketStatusReason,
      websocketStatusCourseId,
      retryConnection,
      sendWebsocketMessage,
    }),
    [
      sharedbConnection,
      websocketStatus,
      websocketStatusReason,
      websocketStatusCourseId,
      retryConnection,
      sendWebsocketMessage,
    ],
  );

  useEffect(() => {
    if (!jwt || !userId) return;
    if (!courseId && !isHomeView) return;

    // Clear the global connection state immediately when starting a new connection
    // This ensures old connection handlers see the state is cleared even if they fire
    // synchronously during cleanup
    globalShareDBState.connection = null;

    if (sharedbConnection) {
      logInfoWithDebugFlag(
        "debug_sharedb",
        "Closing old sharedb connection before creating a new one",
        { courseId },
      );
      // We don't guarantee objects outside of our current websocket course scope to be updated via pubsub, so caches
      // might get stale. We clear them on scope change to prevent that.
      // TODO: We might want to make this more granular, e.g. keep org-scoped data in the cache.
      clearPubsubAPICache();

      // Close the old connection to prevent the socket from reconnecting.
      // This handles the case where a socket was reconnected after cleanup already ran but where the socket is
      // orphaned.
      sharedbConnection.close();
    }

    const socketUrl = `${window._env_.SHAREDB_WEBSOCKET_URL}?jwt=${jwt}&course_id=${courseId}`;
    const socket = new ReconnectingWebSocket(`${socketUrl}`, [], {
      // ShareDB handles dropped messages, and buffering them while the socket
      // is closed has undefined behavior
      maxRetries: MAX_RETRY_TIMES,
      maxEnqueuedMessages: 0,
      minReconnectionDelay: MIN_RECONNECTION_DELAY_MS,
    });

    setWebSocket(socket);

    // Setup socket event listeners
    setupSocketListeners(socket, {
      courseId,
      userId,
      dispatch,
      onError: setWebsocketErrorCode,
    });

    // Initialize status for new connection
    setSharedbStatus("connecting");
    setWebsocketStatusCourseId(courseId || "");

    // It's important to initiate the connection on the socket _before_ the socket
    // finishes connecting, otherwise sharedb will miss the first message event and get stuck.
    const _shareDB = new Connection(socket as Socket);

    // Store the active connection in the global state so we can track it
    globalShareDBState.connection = _shareDB;
    setSharedbConnection(_shareDB);

    // Setup ShareDB connection events using the extracted handler
    const handleShareDBStateChange = (newState: ConnectionState, reason: string) => {
      // Only update status if this is still the active connection
      if (_shareDB === globalShareDBState.connection) {
        logInfoWithDebugFlag("debug_sharedb", "Active ShareDB connection state changed", {
          courseId,
          userId,
          newState,
          reason,
        });

        setWebsocketStatusReason(reason);
        setWebsocketStatusCourseId(courseId || "");
        setSharedbStatus(newState);
      } else {
        logInfoWithDebugFlag("debug_sharedb", "Old ShareDB connection state changed", {
          courseId,
          userId,
          newState,
          reason,
        });
      }
    };

    setupConnectionListeners(_shareDB, {
      courseId,
      userId,
      onStateChange: handleShareDBStateChange,
      onError: setWebsocketErrorCode,
    });

    return () => {
      // Clear global state before closing so old handlers don't see themselves as active
      // even if they fire synchronously during close()
      if (globalShareDBState.connection === _shareDB) {
        globalShareDBState.connection = null;
      }

      // Sharedb is responsible for cleaning up the socket, so we don't close it separately
      logInfoWithDebugFlag(
        "debug_sharedb",
        "Closing ShareDB connection for course due to unmount",
        { courseId },
      );
      _shareDB.close();
    };

    // This component only uses "sharedbConnection" to do a conditional log, and also sets it,
    // so we don't want to cause a loop by re-rendering when it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwt, userId, courseId, isHomeView, connectionKey]);

  return (
    <PubsubStatsCollectorProvider>
      {webSocket && <PingComponent socket={webSocket} />}
      <WebsocketContext.Provider value={contextValue}>{children}</WebsocketContext.Provider>
    </PubsubStatsCollectorProvider>
  );
};
